import { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  authenticatedFetch,
  getConfiguredApiBase,
  getYDocNodePresence,
} from '../../../../api/client'
import { useActiveCanvasId } from '../../../../contexts/ActiveCanvasIdContext'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { createNadDebug } from '../../../../utils/debugLog'
import { useRegisterNodeContextAction } from '../../contextMenu/nodeContextActions'

const log = createNadDebug('dw:library-save-canvas-routing:request')

export type SaveToLibraryState = 'idle' | 'loading' | 'success' | 'error'

const FETCH_TIMEOUT_MS = 15_000
const ERROR_REVERT_MS = 3_000
const YDOC_SYNC_POLL_MS = 200
const YDOC_SYNC_MAX_ATTEMPTS = 15 // ~3s for freshly extracted frames

interface UseSaveToLibraryOptions {
  nodeId: string
  selected: boolean | undefined
}

/**
 * Save one canvas node through the shared Asset Library API and expose the
 * toolbar/context-menu state for its idle, loading, success, and error phases.
 */
export function useSaveToLibrary({ nodeId, selected }: UseSaveToLibraryOptions) {
  const { t } = useTranslation('canvas')
  const projectId = useProjectId()
  const canvasId = useActiveCanvasId()
  const [saveState, setSaveState] = useState<SaveToLibraryState>('idle')
  const abortRef = useRef<AbortController | null>(null)

  const handleSaveToLibrary = useCallback(async () => {
    if (saveState !== 'idle') return
    if (!projectId) {
      toast.error(t('saveToLibrary.noProjectContext'))
      return
    }

    const nonce = Array.from(
      crypto.getRandomValues(new Uint32Array(2)),
      (value) => value.toString(16).padStart(8, '0'),
    ).join('')
    const traceId = `library-save:${projectId}:${canvasId}:${nodeId}:${nonce}`

    log({
      event: 'library-save-canvas-routing:request_started',
      trace_id: traceId,
      node_id: nodeId,
      source_project_id: projectId,
      source_canvas_id: canvasId,
    })
    setSaveState('loading')

    const controller = new AbortController()
    abortRef.current = controller
    let didTimeOut = false
    const timeout = window.setTimeout(() => {
      didTimeOut = true
      controller.abort()
    }, FETCH_TIMEOUT_MS)
    const resetAfterAbort = (phase: 'preflight' | 'request' | 'response') => {
      log({
        event: 'library-save-canvas-routing:request_aborted',
        trace_id: traceId,
        node_id: nodeId,
        source_project_id: projectId,
        source_canvas_id: canvasId,
        phase,
        timed_out: didTimeOut,
      })
      setSaveState('idle')
      if (didTimeOut) toast.error(t('saveToLibrary.timedOut'))
    }

    // Ensure the node has synced to backend YDoc before POST.
    // Extracted frames are created via ReactFlow setNodes + addNodeToYDoc
    // which syncs over y-websocket async. Without this poll, a fast click
    // can POST before the backend room has the node, leading to 404.
    try {
      let synced = false
      for (let i = 0; i < YDOC_SYNC_MAX_ATTEMPTS; i++) {
        if (controller.signal.aborted) break
        try {
          const presence = await getYDocNodePresence(
            projectId,
            canvasId,
            [nodeId],
            { signal: controller.signal },
          )
          const missing = presence.missing_node_ids ?? []
          if (!missing.includes(nodeId)) {
            synced = true
            log({
              event: 'library-save-canvas-routing:ydoc_sync_ready',
              trace_id: traceId,
              node_id: nodeId,
              source_project_id: projectId,
              source_canvas_id: canvasId,
              attempts: i + 1,
            })
            break
          }
        } catch {
          // Presence check is best-effort; proceed to save on failure
          break
        }
        await new Promise((r) => window.setTimeout(r, YDOC_SYNC_POLL_MS))
      }
      if (!synced) {
        log({
          event: 'library-save-canvas-routing:ydoc_sync_not_ready_proceeding',
          trace_id: traceId,
          node_id: nodeId,
          source_project_id: projectId,
          source_canvas_id: canvasId,
        })
      }
    } catch {
      // Ignore sync-check errors
    }

    if (controller.signal.aborted) {
      resetAfterAbort('preflight')
      window.clearTimeout(timeout)
      abortRef.current = null
      return
    }

    try {
      const resp = await authenticatedFetch(`${getConfiguredApiBase()}/library/nodes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source_project_id: projectId, canvas_id: canvasId, node_id: nodeId }),
        signal: controller.signal,
      })
      if (controller.signal.aborted) {
        resetAfterAbort('response')
        return
      }
      if (!resp.ok) {
        const err = await resp.json().catch(() => null)
        throw new Error(err?.detail || resp.statusText)
      }
      log({
        event: 'library-save-canvas-routing:request_succeeded',
        trace_id: traceId,
        node_id: nodeId,
        source_project_id: projectId,
        source_canvas_id: canvasId,
      })
      setSaveState('success')
      toast.success(t('saveToLibrary.savedToast'))
    } catch (err) {
      if (controller.signal.aborted) {
        resetAfterAbort('request')
        return
      }
      const msg = err instanceof Error ? err.message : t('saveToLibrary.unknownError')
      log({
        event: 'library-save-canvas-routing:request_failed',
        trace_id: traceId,
        node_id: nodeId,
        source_project_id: projectId,
        source_canvas_id: canvasId,
        error: msg,
      })
      setSaveState('error')
      toast.error(t('saveToLibrary.failed', { message: msg }))
      window.setTimeout(() => {
        setSaveState((prev) => (prev === 'error' ? 'idle' : prev))
      }, ERROR_REVERT_MS)
    } finally {
      window.clearTimeout(timeout)
      abortRef.current = null
    }
  }, [saveState, nodeId, projectId, canvasId, t])

  useEffect(() => {
    if (!selected && (saveState === 'success' || saveState === 'error')) {
      log('reset nodeId=%s reason=deselect', nodeId)
      setSaveState('idle')
    }
  }, [selected, saveState, nodeId])

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
    }
  }, [])

  const saveLabel =
    saveState === 'loading'
      ? t('saveToLibrary.saving')
      : saveState === 'success'
        ? t('saveToLibrary.saved')
        : t('saveToLibrary.save')

  // Every node that can save to the library calls this hook, so it is also
  // where the node right-click menu's "Save to Library" item is offered.
  useRegisterNodeContextAction(nodeId, 'saveToLibrary', () => { void handleSaveToLibrary() }, saveState === 'idle')

  return { saveState, handleSaveToLibrary, saveLabel }
}
